import React, { useState, useEffect } from "react";
import { toast } from "react-toastify";
import { MdDelete, MdAdd, MdArrowBack } from "react-icons/md";
import { FiRefreshCw } from "react-icons/fi";
import { FaChartLine, FaChartBar } from "react-icons/fa";
import { useMarketSnapshot } from "../../context/MarketSnapshotContext";
import { useEmaStrategy } from "../../context/EmaStrategyContext";
import { useAuth } from "../../context/AuthNewContext";
import "./EmaStratDashboard.css";

const SUPPORTED_INDICES = [
  { displayName: "NIFTY 50", symbol: "NIFTY", securityId: "13" },
  { displayName: "NIFTY BANK", symbol: "BANKNIFTY", securityId: "25" },
  { displayName: "NIFTY IT", symbol: "NIFTYIT", securityId: "29" },
  // { displayName: "NIFTY MIDCAP 100", symbol: "NIFTYMIDCAP", securityId: "37" },
  // { displayName: "NIFTY MIDCAP 50", symbol: "MIDCAP50", securityId: "20" },
  { displayName: "NIFTY NEXT 50", symbol: "NIFTYNEXT50", securityId: "38" },
  { displayName: "NIFTY MIDCAP 150", symbol: "MIDCAP150", securityId: "1" },
];

const EmaStratDashboard = () => {
  const {
    watchlist,
    openTrades,
    closedToday,
    todaySummary,
    signals,
    livePrices,
    isLive,
    connectionStatus,
    lastTickTime,
    loading,
    error,
    addSymbol,
    removeSymbol,
    refresh,
  } = useEmaStrategy();

  const { loadDashboardIndexMarket, dashboardMarketData, dashboardLoading } =
    useMarketSnapshot();

  const [step, setStep] = useState("choose-type");
  const [drilledIndex, setDrilledIndex] = useState("");
  const [busy, setBusy] = useState(false);

  const { user } = useAuth();
  const userID = user?.userID;

  useEffect(() => {
    if (watchlist.length > 0 && step === "choose-type") setStep("dashboard");
  }, [watchlist.length]); // eslint-disable-line

  const handleDrill = async (indexName) => {
    setDrilledIndex(indexName);
    await loadDashboardIndexMarket({ indexName });
  };

  // const handleAdd = async (payload) => {
  //   console.log("[handleAdd] payload:", payload);
  //   setBusy(true);
  //   try {
  //     await addSymbol(payload);

  //     toast.success(`${payload.symbol} added`);
  //     setStep("dashboard");
  //   } catch {
  //     toast.error(`Failed to add ${payload.symbol}`);
  //   } finally {
  //     setBusy(false);
  //   }
  // };
  const handleAdd = async (payload) => {
    console.log("[1] handleAdd START", payload);
    setBusy(true);
    try {
      console.log("[2] calling addSymbol");
      const result = await addSymbol(payload);
      console.log("[3] addSymbol returned:", result);
      toast.success(`${payload.symbol} added`);
      setStep("dashboard");
    } catch (e) {
      console.error("[4] addSymbol THREW:", e.message, e);
      toast.error(`Failed to add ${payload.symbol}`);
    } finally {
      console.log("[5] handleAdd END");
      setBusy(false);
    }
  };
  const handleRemove = async (symbol) => {
    setBusy(true);
    try {
      await removeSymbol(symbol);
      toast.success(`${symbol} removed`);
      if (watchlist.length === 1) setStep("choose-type");
    } catch {
      toast.error(`Failed to remove ${symbol}`);
    } finally {
      setBusy(false);
    }
  };
  console.log("DashboardMakret data", dashboardMarketData);

  const indexConstituents = Object.values(dashboardMarketData || {})
    .map((it) => ({ symbol: it?.symbol, securityId: it?.security_id }))
    .filter((s) => s.symbol && s.securityId);
  console.log("IndexConstitnet", indexConstituents);
  return (
    <div className="ema-dashboard">
      {error && <div className="ema-error">⚠ {String(error)}</div>}

      {/* ─── STEP 1: Choose type ─── */}
      {step === "choose-type" && (
        <div className="ema-step ema-choose-type">
          <h2>What do you want to track?</h2>
          <p className="ema-sub">
            Pick a category to start building your watchlist.
          </p>
          <div className="ema-type-grid">
            <button
              className="ema-type-card"
              onClick={() => {
                setStep("pick-stocks");
                setDrilledIndex("");
              }}
            >
              <FaChartLine className="ema-type-icon" />
              <h3>Trade Stocks</h3>
              <p>
                Buy signals will open <b>paper trades</b> with SL, target and
                trailing.
              </p>
              <span className="ema-type-badge tradable">Tradable</span>
            </button>
            <button
              className="ema-type-card"
              onClick={() => setStep("pick-indices")}
            >
              <FaChartBar className="ema-type-icon" />
              <h3>Track Indices</h3>
              <p>
                Watch NIFTY / BANKNIFTY. Signals only, <b>no trades</b>.
              </p>
              <span className="ema-type-badge signal">Signal-only</span>
            </button>
          </div>
          {watchlist.length > 0 && (
            <button
              className="ema-back-btn"
              onClick={() => setStep("dashboard")}
            >
              <MdArrowBack /> Back to dashboard
            </button>
          )}
        </div>
      )}

      {/* ─── STEP 2a: Pick stocks ─── */}
      {step === "pick-stocks" && (
        <div className="ema-step">
          <div className="ema-step-header">
            <button
              className="ema-back-btn"
              onClick={() => setStep("choose-type")}
            >
              <MdArrowBack /> Back
            </button>
            <h2>Add stocks to watch</h2>
          </div>
          <p className="ema-sub">
            Pick an index → click a stock from its constituents.
          </p>

          <div className="ema-index-grid">
            {SUPPORTED_INDICES.map((idx) => (
              <button
                key={idx.displayName}
                className={`ema-index-card ${drilledIndex === idx.displayName ? "active" : ""}`}
                onClick={() => handleDrill(idx.displayName)}
              >
                {idx.displayName}
              </button>
            ))}
          </div>

          {dashboardLoading && <p className="ema-hint">Loading stocks…</p>}

          {!dashboardLoading &&
            drilledIndex &&
            indexConstituents.length > 0 && (
              <>
                <h4 className="ema-section-title">
                  Constituents of {drilledIndex}
                </h4>
                <div className="ema-stock-grid">
                  {indexConstituents.map((stock) => {
                    const already = watchlist.some(
                      (w) => w.symbol === stock.symbol,
                    );
                    return (
                      <button
                        key={stock.symbol}
                        className={`ema-stock-card ${already ? "added" : ""}`}
                        disabled={already || busy}
                        onClick={() =>
                          handleAdd({
                            symbol: stock.symbol,
                            securityId: stock.securityId,
                            segment: "NSE_EQ",
                            instrument: "EQUITY",
                          })
                        }
                      >
                        {stock.symbol}
                        {already && <span className="ema-added">✓</span>}
                      </button>
                    );
                  })}
                </div>
              </>
            )}
        </div>
      )}

      {/* ─── STEP 2b: Pick indices ─── */}
      {step === "pick-indices" && (
        <div className="ema-step">
          <div className="ema-step-header">
            <button
              className="ema-back-btn"
              onClick={() => setStep("choose-type")}
            >
              <MdArrowBack /> Back
            </button>
            <h2>Add indices to watch</h2>
          </div>
          <p className="ema-note">
            Indices are <b>signal-only</b>. No paper trades will be created.
          </p>

          <div className="ema-index-grid">
            {SUPPORTED_INDICES.map((idx) => {
              const already = watchlist.some((w) => w.symbol === idx.symbol);
              return (
                <button
                  key={idx.displayName}
                  className={`ema-index-card ${already ? "added" : ""}`}
                  disabled={already || busy}
                  onClick={() =>
                    handleAdd({
                      symbol: idx.symbol,
                      securityId: idx.securityId,
                      segment: "IDX_I",
                      instrument: "INDEX",
                    })
                  }
                >
                  {idx.displayName}
                  {already && <span className="ema-added">✓</span>}
                </button>
              );
            })}
          </div>
        </div>
      )}

      {/* ─── STEP 3: Live dashboard ─── */}
      {step === "dashboard" && (
        <div className="ema-step ema-live">
          {/* Watchlist with live prices from SSE */}
          <div className="ema-watchlist-bar">
            <div className="ema-watchlist-header">
              <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                <h3 style={{ margin: 0 }}>Your watchlist</h3>
                <span
                  className={`ema-live-pill ${connectionStatus || (isLive ? "live" : "disconnected")}`}
                  title={
                    lastTickTime
                      ? `Last tick ${new Date(lastTickTime).toLocaleTimeString("en-IN")}`
                      : "No ticks yet"
                  }
                >
                  <span className="ema-live-dot" />
                  {connectionStatus === "live" || isLive
                    ? "LIVE"
                    : connectionStatus === "closed"
                      ? "MARKET CLOSED"
                      : connectionStatus === "connecting"
                        ? "CONNECTING…"
                        : "OFFLINE"}
                </span>
              </div>
              <div style={{ display: "flex", gap: 8 }}>
                <button
                  className="ema-refresh-btn"
                  onClick={refresh}
                  title="Force reload from DB"
                >
                  <FiRefreshCw />
                </button>
                <button
                  className="ema-add-btn"
                  onClick={() => setStep("choose-type")}
                >
                  <MdAdd /> Add more
                </button>
              </div>
            </div>
            <div className="ema-chips">
              {watchlist.map((w) => {
                const price = livePrices?.[w.symbol]?.ltp;
                return (
                  <span
                    key={w.symbol}
                    className={`ema-chip ${w.segment === "IDX_I" ? "idx" : "stk"}`}
                  >
                    {w.symbol}
                    <span className="ema-chip-tag">
                      {w.segment === "IDX_I" ? "IDX" : "EQ"}
                    </span>
                    {Number.isFinite(Number(price)) && (
                      <span className="ema-chip-price">
                        ₹{Number(price).toFixed(2)}
                      </span>
                    )}
                    <MdDelete
                      className="ema-chip-x"
                      onClick={() => handleRemove(w.symbol)}
                    />
                  </span>
                );
              })}
            </div>
          </div>

          {/* Summary strip */}
          <div className="ema-summary">
            <div className="ema-summary-card">
              <label>Today's P&L</label>
              <div
                className={`ema-summary-value ${
                  todaySummary.total_pnl > 0
                    ? "pos"
                    : todaySummary.total_pnl < 0
                      ? "neg"
                      : ""
                }`}
              >
                ₹{Number(todaySummary.total_pnl || 0).toFixed(2)}
              </div>
            </div>
            <div className="ema-summary-card">
              <label>Open</label>
              <div className="ema-summary-value">{todaySummary.open_count}</div>
            </div>
            <div className="ema-summary-card">
              <label>Closed</label>
              <div className="ema-summary-value">
                {todaySummary.closed_count}
              </div>
            </div>
            <div className="ema-summary-card">
              <label>Win / Loss</label>
              <div className="ema-summary-value">
                {todaySummary.win_count} / {todaySummary.loss_count}
              </div>
            </div>
          </div>

          {/* Open positions with LIVE P&L */}
          <div className="ema-panel">
            <h3>Open positions ({openTrades.length})</h3>
            {openTrades.length === 0 ? (
              <p className="ema-empty">No open positions right now.</p>
            ) : (
              <div className="ema-table-wrap">
                <table className="ema-table">
                  <thead>
                    <tr>
                      <th>Symbol</th>
                      <th>Entry</th>
                      <th>LTP</th>
                      <th>Live P&L</th>
                      <th>Qty</th>
                      <th>SL</th>
                      <th>Target</th>
                      <th>Status</th>
                      <th>Time</th>
                    </tr>
                  </thead>
                  <tbody>
                    {openTrades.map((t) => {
                      const rawLtp =
                        livePrices?.[t.symbol]?.ltp ?? t.snapshot_ltp ?? null;
                      const ltp = rawLtp !== null ? Number(rawLtp) : null;
                      const hasLtp = Number.isFinite(ltp);
                      const livePnl = hasLtp
                        ? (ltp - Number(t.entry_price)) * Number(t.quantity)
                        : null;
                      const isProfit = livePnl !== null && livePnl >= 0;
                      return (
                        <tr key={t.id}>
                          <td>
                            <b>{t.symbol}</b>
                          </td>
                          <td>₹{Number(t.entry_price).toFixed(2)}</td>
                          <td>
                            {hasLtp ? (
                              <span className={isProfit ? "pos" : "neg"}>
                                ₹{ltp.toFixed(2)}
                              </span>
                            ) : (
                              <span className="muted">—</span>
                            )}
                          </td>
                          <td>
                            {livePnl !== null ? (
                              <span className={isProfit ? "pos" : "neg"}>
                                {isProfit ? "+" : ""}₹
                                {Math.abs(livePnl).toFixed(0)}
                              </span>
                            ) : (
                              "—"
                            )}
                          </td>
                          <td>{t.quantity}</td>
                          <td>₹{Number(t.stop_loss).toFixed(2)}</td>
                          <td>
                            ₹
                            {Number(t.target_price || t.target || 0).toFixed(2)}
                          </td>
                          <td>
                            {t.trailing_active ? (
                              <span className="ema-badge trail">Trailing</span>
                            ) : t.target_hit ? (
                              <span className="ema-badge be">Breakeven</span>
                            ) : (
                              <span className="ema-badge">Open</span>
                            )}
                          </td>
                          <td>
                            {new Date(t.entry_time).toLocaleTimeString(
                              "en-IN",
                              {
                                hour: "2-digit",
                                minute: "2-digit",
                              },
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          {/* Recent signals */}
          <div className="ema-panel">
            <h3>Recent signals ({signals.length})</h3>
            {signals.length === 0 ? (
              <p className="ema-empty">No signals yet.</p>
            ) : (
              <div className="ema-signals">
                {signals.map((s) => (
                  <div
                    key={s.id}
                    className={`ema-signal-row kind-${(s.kind || "").toLowerCase()}`}
                  >
                    <span className="ema-sig-time">
                      {new Date(s.created_at).toLocaleTimeString("en-IN", {
                        hour: "2-digit",
                        minute: "2-digit",
                        second: "2-digit",
                      })}
                    </span>
                    <span className="ema-sig-sym">{s.symbol}</span>
                    <span className="ema-sig-seg">{s.segment}</span>
                    <span className="ema-sig-kind">{s.kind}</span>
                    <span className="ema-sig-ltp">
                      ₹{Number(s.ltp || 0).toFixed(2)}
                    </span>
                    <span className={`ema-sig-macd ${s.macd_state}`}>
                      MACD {s.macd_state}
                    </span>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Closed today */}
          {closedToday.length > 0 && (
            <div className="ema-panel">
              <h3>Today's closed trades</h3>
              <div className="ema-table-wrap">
                <table className="ema-table">
                  <thead>
                    <tr>
                      <th>Symbol</th>
                      <th>Entry</th>
                      <th>Exit</th>
                      <th>P&L</th>
                      <th>Reason</th>
                    </tr>
                  </thead>
                  <tbody>
                    {closedToday.map((t) => (
                      <tr key={t.id}>
                        <td>
                          <b>{t.symbol}</b>
                        </td>
                        <td>₹{Number(t.entry_price).toFixed(2)}</td>
                        <td>₹{Number(t.exit_price).toFixed(2)}</td>
                        <td
                          className={Number(t.pnl_amount) >= 0 ? "pos" : "neg"}
                        >
                          ₹{Number(t.pnl_amount).toFixed(2)}
                        </td>
                        <td className="ema-reason">{t.exit_reason}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
      )}

      {loading && <div className="ema-loading">Loading…</div>}
    </div>
  );
};

export default EmaStratDashboard;
